import {
  Box,
  Container,
  Flex,
  HStack,
  Image,
  Link,
  Select,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
  keyframes,
  Icon,
} from '@chakra-ui/react'
import { useAppContext } from './hooks/AppContextProvider'
import { useNavigate, useLocation, useMatch } from 'react-router-dom'
import { IoIosLogIn, IoIosLogOut } from 'react-icons/io'
import { FaBook } from 'react-icons/fa'
import { useMemo } from 'react'
import SidebarMenu from './components/SidebarMenu'
import ViewTenantModeButton from './components/ViewTenantModeButton'
import { useTranslation } from 'react-i18next'
import QRLayout from './layouts/QRLayout'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Layout = ({ renderRouter }: any) => {
  const {
    isAuthenticated,
    logout,
    language,
    updateLanguage,
    emailUser,
    username,
    headerCenterMessage,
    customLogoUrl,
    isTenant,
    isMaster,
  } = useAppContext()
  const navigation = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const domain = useMemo<string>(() => {
    const dmSplit = emailUser?.split('@')

    return dmSplit?.length ? dmSplit[dmSplit.length - 1] : ''
  }, [emailUser])

  const userDomain = useMemo<string>(() => {
    return username || emailUser
  }, [emailUser, username])

  const userLink = useMemo<string>(() => {
    // User role cannot access /:tenant, so redirect to /tenant-domains
    // Tenant and masterTenant can access /:tenant
    if (isTenant || isMaster) {
      return `/${domain || ''}`
    } else {
      return '/tenant-domains'
    }
  }, [domain, isTenant, isMaster])

  const matchUrl = useMatch({ path: '/user/:tenant/:userTenant/:nftId' })
  const shouldShowCustomLogo = useMemo(() => {
    return (
      location.pathname.startsWith('/customize-display-for-product') ||
      Boolean(matchUrl)
    );
  }, [location.pathname, matchUrl]);

  // Check if current route is QR verification route
  const isQRRoute = useMemo(() => {
    return location.pathname.startsWith('/qr/verify')
  }, [location.pathname])

  // Memoize the message component to prevent unnecessary re-renders
  const messageComponent = useMemo(() => {
    if (!headerCenterMessage) return null;

    return (
      <Text
        color="red.500"
        fontWeight="bold"
        fontSize="lg"
        maxW={{ base: "90%", md: "50%" }}
        textAlign="center"
        whiteSpace={{ base: "normal", md: "nowrap" }}
        overflow="hidden"
        textOverflow="ellipsis"
        px={4}
        position="absolute"
        left="50%"
        top="50%"
        transform="translate(-50%, -50%)"
        transition="all 0.3s ease"
        zIndex={1}
        py={1}
        _hover={{
          maxW: { base: "95%", md: "80%" },
          whiteSpace: "normal",
          wordBreak: "break-word",
          boxShadow: "sm"
        }}
      >
        {headerCenterMessage}
      </Text>
    );
  }, [headerCenterMessage]);

  // Memoize logo component for better performance
  const logoComponent = useMemo(() => {
    const defaultLogo = '/assets/logo.png';
    const logoUrl = shouldShowCustomLogo && customLogoUrl ? customLogoUrl : defaultLogo;
    return (
      <Box
        position="relative"
        animation={`${fadeIn} 0.3s ease-in-out`}
        w="209px"
        h="41px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        borderRadius="sm"
      >
        <Image
          src={logoUrl}
          alt={'logo'}
          fallbackSrc={defaultLogo}
          loading="lazy"
          maxW="100%"
          maxH="100%"
          w="auto"
          h="auto"
          objectFit="contain"
          _hover={{
            transform: 'scale(1.02)',
          }}
        />
      </Box>
    );
  }, [customLogoUrl, shouldShowCustomLogo]);

  const handleAction = () => {
    if (isAuthenticated) {
      // Clear all cookies
      const cookies = document.cookie.split(';')
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i]
        const eqPos = cookie.indexOf('=')
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie
        document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/'
      }
      logout()
    } else {
      navigation('/login')
    }
  }

  // Define all useColorModeValue hooks before conditional returns
  const headerBgColor = useColorModeValue('gray.100', 'gray.900')

  const handleSwitchLocale = (locale: any) => {
    updateLanguage(locale.target.value)
  }

  // Use QR Layout for QR verification routes
  if (isQRRoute) {
    return (
      <QRLayout>
        {renderRouter()}
      </QRLayout>
    )
  }

  return (
    <Flex flexDir={'column'} minH={'100vh'}>
      <Box bg={headerBgColor} px={4}>
        <Container
          as={Stack}
          maxW={'8xl'}
          py={4}
          direction={{ base: 'column', md: 'row' }}
          spacing={4}
          justify={{ base: 'center', md: 'space-between' }}
        >
          <Flex
            h={16}
            alignItems={'center'}
            justifyContent={'space-between'}
            w={'full'}
            position="relative"
          >
            <HStack spacing={8} alignItems={'center'}>
              <Box
                textTransform={'uppercase'}
                fontWeight={'bold'}
                fontSize={'1.5rem'}
              >
                <Link href={userLink} _hover={{ textDecoration: 'none' }}>
                  {logoComponent}
                </Link>
              </Box>
            </HStack>
            {messageComponent}
            <Flex alignItems={'center'} gap={[2, 2, 4, 4]}>
              {/* API Integration Guideline Link - Only for tenant and master */}
              {(isTenant || isMaster) && (!messageComponent) && (
                <Link
                  href="/api-integration-guideline"
                  onClick={(e) => {
                    e.preventDefault()
                    navigation('/api-integration-guideline')
                  }}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  px={3}
                  py={2}
                  borderRadius="md"
                  bg="blue.50"
                  color="blue.600"
                  fontSize={['0.75rem', '0.75rem', '0.875rem', '0.875rem']}
                  fontWeight="semibold"
                  border="1px solid"
                  borderColor="blue.200"
                  transition="all 0.2s"
                  _hover={{
                    bg: 'blue.100',
                    borderColor: 'blue.300',
                    transform: 'translateY(-1px)',
                    boxShadow: 'sm'
                  }}
                  _active={{
                    transform: 'translateY(0)',
                  }}
                >
                  <Icon as={FaBook} boxSize={[3, 3, 4, 4]} />
                  <Text display={['none', 'none', 'block', 'block']}>
                    {t('api_integration_guideline.title')}
                  </Text>
                </Link>
              )}
              <Select
                width={'7rem'}
                height={'2rem'}
                borderColor={'#000'}
                onChange={handleSwitchLocale}
                value={language}
              >
                <option value="ja">日本語</option>
                <option value="en">English</option>
              </Select>
              <Flex cursor={'pointer'} onClick={handleAction}>
                <Tooltip label={emailUser}>
                  <Text
                    fontSize={['0.625rem', '0.625rem', '1rem', '1rem']}
                    fontWeight={'semibold'}
                    mr={['0.125rem', '0.125rem', '0.25rem', '0.25rem']}
                  >
                    {userDomain || ''}
                  </Text>
                </Tooltip>
                {isAuthenticated ? (
                  <IoIosLogOut size={'1.5rem'} />
                ) : (
                  <IoIosLogIn size={'1.5rem'} />
                )}
              </Flex>
            </Flex>
          </Flex>
        </Container>
      </Box>
      <Flex flex={1}>
        {isAuthenticated ? <SidebarMenu /> : null}
        <Box flex="1" transition="all 0.3s ease" ml={0}>
          <Container
            as={Stack}
            maxW={'8xl'}
            py={4}
            direction={{ base: 'column', md: 'row' }}
            spacing={4}
            justify={{ base: 'center', md: 'space-between' }}
          >
            {renderRouter()}
          </Container>
        </Box>
      </Flex>
      <ViewTenantModeButton />
    </Flex>
  )
}

export default Layout
